import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import type { RootState } from '../../../slices';
import { getCompanyById } from '../../../slices/companies/thunk';
import { createWaybill } from '../../../slices/waybills/thunk';
import { getCustomersFindOne, updateCustomer } from '../../../slices/customers/thunk';
import { getDescendants, getCatalogs } from '../../../slices/catalogs/thunk';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import { Detail, AdditionalField, RemissionGuideFormValues, Company, Establishment, EmissionPoint } from './types';

type AppDispatch = ThunkDispatch<any, unknown, any>;

const REMISSION_GUIDE_UTILS = {
    mapApiFieldToFormField: (apiField: string): string | null => {
        const fieldMapping: Record<string, string> = {
            'transporterInfo.transporterRuc': 'carrierId',
            'transporterInfo.transportRuc': 'carrierId',
            'transporterInfo.transporterName': 'carrierName',
            'transporterInfo.transporterIdentificationType': 'carrierIdType',
            'transporterInfo.transporterEmail': 'carrierEmail',
            'waybillInfo.departureAddress': 'startingAddress',
            'waybillInfo.licensePlate': 'plate',
            'waybillInfo.startDate': 'startDate',
            'waybillInfo.endDate': 'endDate',
            'receivers.0.identification': 'destinatarioId',
            'receivers.0.reasonTransfer': 'reasonForTransfer',
            'receivers.0.destinationAddress': 'arrivalAddress',
            'receivers.0.route': 'route',
            'receivers.0.customsDocNumber': 'customsDoc',
            'receivers.0.supportDocumentNumber': 'sustentoNum',
            'receivers.0.supportAuthNumber': 'authNumber',
            'receivers.0.supportIssueDate': 'sustentoDate'
        };

        return fieldMapping[apiField] || null;
    },

    formatDateToDMY: (value: string | undefined | null): string => {
        if (!value) return '';
        const trimmedValue = value.trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmedValue)) return trimmedValue;
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
            const [year, month, day] = trimmedValue.split('-');
            return `${day}/${month}/${year}`;
        }
        const date = new Date(trimmedValue);
        if (!isNaN(date.getTime())) {
            const dd = String(date.getDate()).padStart(2, '0');
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const yyyy = String(date.getFullYear());
            return `${dd}/${mm}/${yyyy}`;
        }
        return '';
    },
};

const { mapApiFieldToFormField, formatDateToDMY } = REMISSION_GUIDE_UTILS;

interface JWTPayload {
    id: string;
    companyId: string;
    establishmentId: string;
    emissionPointId: string;
    name: string;
    document: string;
    email?: string;
    roles?: Array<{ name: string }>;
}

const useRemissionGuide = () => {
    const dispatch = useDispatch<AppDispatch>();
    const reduxSelectedCompany = useSelector((state: RootState) => state.Companies.selectedCompany);
    const isCompaniesLoading = useSelector((state: RootState) => state.Companies.isLoading);

    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [selectedEstablishment, setSelectedEstablishment] = useState<Establishment | null>(null);
    const [selectedEmissionPoint, setSelectedEmissionPoint] = useState<EmissionPoint | null>(null);
    const [details, setDetails] = useState<Detail[]>([]);
    const [additionalFields, setAdditionalFields] = useState<AdditionalField[]>([]);
    const [jwtPayload, setJwtPayload] = useState<JWTPayload | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const [documentTypes, setDocumentTypes] = useState<any[]>([]);
    const docTypesLoaded = useRef(false);

    const catalogList = useSelector((state: RootState) => state.Catalogs.catalogList);
    const descendants = useSelector((state: RootState) => state.Catalogs.descendants);

    const getDocumentTypeCode = useCallback((id: string): string => {
        const docType = documentTypes.find((type: any) => type.id === id);
        return docType?.jsonData?.code || '';
    }, [documentTypes]);

    const validationSchema = useMemo(() => Yup.object({
        // Transportista
        carrierIdType: Yup.string().required('El tipo de identificación es requerido'),
        carrierId: Yup.string()
            .required('La identificación es requerida')
            .when('carrierIdType', (carrierIdType, schema) => {
                const type = Array.isArray(carrierIdType) ? carrierIdType[0] : carrierIdType;
                if (type === 'RUC') {
                    return schema.matches(/^\d{13}$/g, 'El RUC debe tener 13 dígitos');
                }
                if (type === 'Cédula') {
                    return schema.matches(/^\d{10}$/g, 'La cédula debe tener 10 dígitos');
                }
                return schema;
            }),
        carrierName: Yup.string().required('La razón social es requerida').matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
        carrierEmail: Yup.string().email('Ingrese un email válido').required('El email es requerido').matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),

        // Traslado
        startingAddress: Yup.string().required('La dirección de partida es requerida').matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
        startDate: Yup.string().required('La fecha de inicio es requerida'),
        endDate: Yup.string().required('La fecha de fin es requerida'),
        plate: Yup.string().required('La placa es requerida').matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),

        // Destinatario
        destinatarioIdType: Yup.string().required('El tipo de identificación es requerido'),
        destinatarioId: Yup.string()
            .required('La identificación es requerida')
            .when('destinatarioIdType', (destinatarioIdType, schema) => {
                const type = Array.isArray(destinatarioIdType) ? destinatarioIdType[0] : destinatarioIdType;
                if (type === 'RUC') {
                    return schema.matches(/^\d{13}$/g, 'El RUC debe tener 13 dígitos');
                }
                if (type === 'Cédula') {
                    return schema.matches(/^\d{10}$/g, 'La cédula debe tener 10 dígitos');
                }
                return schema;
            }),
        destinatarioName: Yup.string().required('La razón social es requerida').matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
        arrivalAddress: Yup.string().required('La dirección destino es requerida').matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
        reasonForTransfer: Yup.string().required('El motivo es requerido').matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),

        // Comprobante Sustento
        emissionType: Yup.string().optional(),
        documentType: Yup.string().optional(),
        authNumber: Yup.string()
            .optional()
            .test('is-10-digits', 'El número de autorización debe tener exactamente 10 dígitos', val => !val || /^\d{10}$/.test(val)),
        sustentoDate: Yup.string().optional(),
        sustentoNum: Yup.string().optional(),

        // Campos opcionales
        route: Yup.string().optional().matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
        customsDoc: Yup.string().optional().matches(/^[^<>&"']*$/, "No se permiten caracteres especiales (<, >, &, \", ')"),
    }), []);

    // useRef estable para acceder a formik dentro de useEffect sin añadirlo a deps
    const formikRef = useRef<ReturnType<typeof useFormik<RemissionGuideFormValues>> | null>(null);

    const formik = useFormik<RemissionGuideFormValues>({
        initialValues: {
            // Transportista
            carrierIdType: '',
            carrierId: '',
            carrierName: '',
            carrierEmail: '',

            // Traslado
            startingAddress: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            plate: '',

            // Destinatario
            destinatarioIdType: '',
            destinatarioId: '',
            destinatarioName: '',
            arrivalAddress: '',
            route: '',
            reasonForTransfer: '',
            customsDoc: '',

            // Comprobante Sustento
            emissionType: 'Física',
            documentType: 'Factura',
            authNumber: '',
            sustentoDate: '',
            sustentoNum: '',
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true);
            try {
                const sanitizeXMLString = (str: string | undefined | null) => {
                    if (!str) return "";
                    return str.replace(/[<>&"']/g, '');
                };

                if (values.carrierId) {
                    try {
                        const carrierResult = await dispatch(getCustomersFindOne({
                            identificationNumber: values.carrierId
                        })).unwrap();

                        let foundCarrier = null;
                        if (Array.isArray(carrierResult.data)) {
                            foundCarrier = carrierResult.data.find((c: any) => c.identificationNumber === values.carrierId);
                        } else if (carrierResult.data && typeof carrierResult.data === 'object') {
                            if (carrierResult.data.identificationNumber === values.carrierId) {
                                foundCarrier = carrierResult.data;
                            }
                        }

                        if (foundCarrier && !foundCarrier.isCarrier) {
                            await dispatch(updateCustomer({
                                customer: {
                                    ...foundCarrier,
                                    isCarrier: true
                                }
                            })).unwrap();

                            toast.success("Transportista actualizado correctamente");
                        }
                    } catch (carrierCheckError) {
                        console.error("Error verificando transportista:", carrierCheckError);
                        toast.warning("No se pudo verificar el estado del transportista, continuando con el envío...");
                    }
                }

                const waybillData = {
                    taxInfo: {
                        docCode: getDocumentTypeCode(values.destinatarioIdType),
                        emissionType: values.emissionType === 'Física' ? '1' : '2'
                    },
                    transporterInfo: {
                        transporterName: sanitizeXMLString(values.carrierName),
                        transporterIdentificationType: getDocumentTypeCode(values.carrierIdType),
                        transporterRuc: values.carrierId,
                    },
                    waybillInfo: {
                        departureAddress: sanitizeXMLString(values.startingAddress),
                        establishmentAddress: selectedEstablishment?.address || sanitizeXMLString(values.startingAddress),
                        startDate: formatDateToDMY(values.startDate),
                        endDate: formatDateToDMY(values.endDate),
                        licensePlate: sanitizeXMLString(values.plate)
                    },
                    receivers: [
                        {
                            identification: values.destinatarioId,
                            identificationType: getDocumentTypeCode(values.destinatarioIdType),
                            receiverName: sanitizeXMLString(values.destinatarioName),
                            reasonTransfer: sanitizeXMLString(values.reasonForTransfer),
                            destinationAddress: sanitizeXMLString(values.arrivalAddress),
                            customsDocNumber: sanitizeXMLString(values.customsDoc) || "",
                            codEstabDestino: selectedEstablishment?.code || "001",
                            route: sanitizeXMLString(values.route) || `${sanitizeXMLString(values.startingAddress)} - ${sanitizeXMLString(values.arrivalAddress)}`,
                            docSupportCode: values.sustentoNum ? (values.documentType === 'Factura' ? '01' : '02') : "",
                            supportDocumentNumber: values.sustentoNum || "",
                            supportAuthNumber: values.authNumber || "",
                            supportIssueDate: values.sustentoNum && values.sustentoDate ? formatDateToDMY(values.sustentoDate) : "",
                            details: details.map(detail => ({
                                internalCode: sanitizeXMLString(detail.code),
                                additionalCode: sanitizeXMLString(detail.auxCode) || "",
                                description: sanitizeXMLString(detail.description),
                                quantity: detail.quantity.toString()
                            }))
                        }
                    ],
                    additionalInfo: [
                        ...(values.carrierEmail ? [{
                            name: 'Email',
                            value: sanitizeXMLString(values.carrierEmail)
                        }] : []),
                        ...additionalFields.map(field => ({
                            name: sanitizeXMLString(field.name),
                            value: sanitizeXMLString(field.description)
                        }))
                    ].filter(f => f.name.trim() !== '')
                };

                const response: any = await dispatch(createWaybill(waybillData)).unwrap();

                if (response?.success === true) {
                    formik.resetForm();
                    setDetails([]);
                    setAdditionalFields([]);

                    setShowSuccessModal(true);
                    toast.success('¡Guía de remisión generada exitosamente!');
                }

            } catch (error: any) {
                console.error('Error creating waybill:', error);

                const apiErrorPayload = error?.error || error;
                const errorArray = apiErrorPayload?.error || apiErrorPayload?.errors;
                if (errorArray) {
                    const apiErrors = errorArray;
                    const formErrors: any = {};
                    const generalErrors: string[] = [];

                    apiErrors.forEach((err: any) => {
                        const fieldName = mapApiFieldToFormField(err.field);
                        if (fieldName) {
                            formErrors[fieldName] = err.message;
                        } else {
                            const fieldLabel = err.field || 'Error';
                            generalErrors.push(`${fieldLabel}: ${err.message}`);
                        }
                    });

                    Object.keys(formErrors).forEach(field => {
                        formik.setFieldError(field, formErrors[field]);
                        formik.setFieldTouched(field, true, false);
                    });

                    if (generalErrors.length > 0) {
                        formik.setStatus({ apiErrors: generalErrors });
                    }
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Mantener el ref siempre actualizado con la instancia actual de formik
    formikRef.current = formik;

    useEffect(() => {
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
            try {
                const { token } = JSON.parse(authUser);
                if (token) {
                    const decoded: JWTPayload = jwtDecode(token);
                    const companyId = decoded.companyId;
                    if (companyId) {
                        setJwtPayload(decoded);
                        dispatch(getCompanyById(companyId));
                    }
                }
            } catch (e) {
                console.error("Failed to parse authUser or decode token", e);
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }
        setIsInitialized(true);

        dispatch(getCatalogs());
    }, [dispatch]);

    useEffect(() => {
        if (catalogList && catalogList.length > 0 && !docTypesLoaded.current) {
            const idCatalog = catalogList.find((c: any) => c.name === 'Tipos de identificación');
            if (idCatalog) {
                dispatch(getDescendants(idCatalog.id));
                docTypesLoaded.current = true;
            }
        }
    }, [catalogList, dispatch]);

    useEffect(() => {
        if (descendants && Object.keys(descendants).length > 0) {
            const idCatalog = catalogList?.find((c: any) => c.name === 'Tipos de identificación');
            if (idCatalog && descendants[idCatalog.id]) {
                setDocumentTypes(descendants[idCatalog.id]);
            }
        }
    }, [descendants, catalogList]);

    useEffect(() => {
        if (reduxSelectedCompany && !selectedCompany) {
            setSelectedCompany(reduxSelectedCompany);
        }

        if (!isCompaniesLoading && (reduxSelectedCompany || isInitialized)) {
            setIsLoading(false);
        }
    }, [reduxSelectedCompany, isCompaniesLoading, selectedCompany, isInitialized]);

    useEffect(() => {
        if (!isInitialized || !jwtPayload || !selectedCompany || !selectedCompany.establishments) {
            return;
        }

        if (selectedEstablishment) {
            return;
        }

        const establishmentFromToken = selectedCompany.establishments.find((establishment: Establishment) =>
            establishment.id === jwtPayload.establishmentId ||
            establishment._id === jwtPayload.establishmentId
        );

        if (establishmentFromToken) {
            setSelectedEstablishment(establishmentFromToken);
        } else if (selectedCompany.establishments.length > 0) {
            setSelectedEstablishment(selectedCompany.establishments[0]);
        }
    }, [isInitialized, jwtPayload, selectedCompany, selectedEstablishment]);

    useEffect(() => {
        if (!isInitialized || !jwtPayload || !selectedEstablishment || !selectedEstablishment.emissionPoints) {
            return;
        }

        if (selectedEmissionPoint) {
            return;
        }

        const emissionPointFromToken = selectedEstablishment.emissionPoints.find((point: any) =>
            point.id === jwtPayload.emissionPointId ||
            point._id === jwtPayload.emissionPointId
        );

        if (emissionPointFromToken) {
            const emissionPointWithName = {
                ...emissionPointFromToken,
                name: emissionPointFromToken.name || `Punto ${emissionPointFromToken.code}`
            };
            setSelectedEmissionPoint(emissionPointWithName);
        } else if (selectedEstablishment.emissionPoints.length > 0) {
            const firstEmissionPoint = {
                ...selectedEstablishment.emissionPoints[0],
                name: selectedEstablishment.emissionPoints[0].name || `Punto ${selectedEstablishment.emissionPoints[0].code}`
            };
            setSelectedEmissionPoint(firstEmissionPoint);
        }
    }, [isInitialized, jwtPayload, selectedEstablishment, selectedEmissionPoint]);

    useEffect(() => {
        const copiedRemissionData = sessionStorage.getItem('copiedRemissionData');
        if (copiedRemissionData && documentTypes.length > 0 && isInitialized) {
            try {
                const data = JSON.parse(copiedRemissionData);
                sessionStorage.removeItem('copiedRemissionData');

                // Construir todos los valores de una vez y aplicarlos en un solo setValues
                // para evitar ~17 re-renders separados (uno por cada setFieldValue)
                const newValues: Partial<RemissionGuideFormValues> = {};

                if (data.transporterInfo) {
                    const transporter = data.transporterInfo;
                    const docType = documentTypes.find((dt: any) => dt.jsonData?.code === transporter.tipoIdentificacionTransportista);
                    newValues.carrierIdType = docType?.id || '';
                    newValues.carrierId = transporter.rucTransportista || '';
                    newValues.carrierName = transporter.razonSocialTransportista || '';

                    const emailField = data.additionalInfo?.find((field: any) =>
                        field.nombre?.toLowerCase().includes('email') || field.nombre?.toLowerCase().includes('correo')
                    );
                    if (emailField) {
                        newValues.carrierEmail = emailField.valor || '';
                    }
                }

                if (data.waybillInfo) {
                    const waybill = data.waybillInfo;
                    newValues.startingAddress = waybill.dirPartida || '';
                    newValues.plate = waybill.placa || '';
                }

                if (data.receivers && data.receivers.length > 0) {
                    const receiver = data.receivers[0];
                    const destDocType = documentTypes.find((dt: any) => dt.jsonData?.code === receiver.tipoIdentificacionDestinatario);
                    newValues.destinatarioIdType = destDocType?.id || '';
                    newValues.destinatarioId = receiver.identificacionDestinatario || '';
                    newValues.destinatarioName = receiver.razonSocialDestinatario ||
                        receiver.nombreDestinatario ||
                        receiver.nombre ||
                        receiver.name ||
                        receiver.razonSocial || '';
                    newValues.arrivalAddress = receiver.dirDestinatario || receiver.direccion || '';
                    newValues.route = receiver.ruta || '';
                    newValues.reasonForTransfer = receiver.motivoTraslado || '';
                    newValues.sustentoNum = receiver.numDocSustento || '';
                    newValues.authNumber = receiver.numAutDocSustento || '';
                    newValues.customsDoc = '';
                }

                // Un solo setValues = un solo re-render en lugar de ~17
                if (formikRef.current && Object.keys(newValues).length > 0) {
                    formikRef.current.setValues({
                        ...formikRef.current.values,
                        ...newValues,
                    });
                }

                if (data.details && Array.isArray(data.details)) {
                    setDetails(data.details.map((detail: any) => ({
                        id: crypto.randomUUID(),
                        code: detail.codigoInterno || '',
                        auxCode: detail.codigoAdicional || '',
                        description: detail.descripcion || '',
                        quantity: parseFloat(detail.cantidad) || 1,
                    })));
                }

                if (data.additionalInfo && Array.isArray(data.additionalInfo)) {
                    const excludeFields = ['Email', 'email'];
                    setAdditionalFields(
                        data.additionalInfo
                            .filter((field: any) => field.nombre && field.nombre.trim() !== '-' && !excludeFields.includes(field.nombre))
                            .map((field: any) => ({
                                id: crypto.randomUUID(),
                                name: field.nombre || '',
                                description: field.valor || '',
                            }))
                    );
                }

                toast.success('Datos de guía copiados correctamente');
            } catch (error) {
                console.error('Error al cargar datos copiados:', error);
                toast.error('Error al cargar datos copiados');
            }
        }
        // formikRef es estable (no cambia su referencia), así evitamos re-ejecuciones
        // en cada render causadas por el objeto formik siendo nuevo en cada ciclo.
    }, [documentTypes, isInitialized]);

    const addEmptyDetail = useCallback(() => {
        const newDetail: Detail = {
            id: crypto.randomUUID(),
            code: "",
            auxCode: "",
            description: "",
            quantity: 1,
        };
        setDetails(prev => [...prev, newDetail]);
    }, []);

    const removeDetail = useCallback((id: string) => {
        setDetails(prev => prev.filter((d) => d.id !== id));
    }, []);

    const updateDetail = useCallback((id: string, field: keyof Detail, value: any) => {
        setDetails(prev => prev.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
    }, []);

    const addEmptyAdditionalField = useCallback(() => {
        const newField: AdditionalField = {
            id: crypto.randomUUID(),
            name: "",
            description: "",
        };
        setAdditionalFields(prev => [...prev, newField]);
    }, []);

    const removeAdditionalField = useCallback((id: string) => {
        setAdditionalFields(prev => prev.filter((f) => f.id !== id));
    }, []);

    const updateAdditionalField = useCallback((id: string, field: 'name' | 'description', value: string) => {
        setAdditionalFields(prev => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
    }, []);

    const handleCompanyChange = useCallback((company: Company) => {
        setSelectedCompany(company);
    }, []);

    const handleEstablishmentChange = useCallback((establishment: Establishment) => {
        setSelectedEstablishment(establishment);
    }, []);

    const handleEmissionPointChange = useCallback((emissionPoint: EmissionPoint) => {
        setSelectedEmissionPoint(emissionPoint);
    }, []);

    return {
        selectedCompany,
        selectedEstablishment,
        selectedEmissionPoint,
        details,
        additionalFields,
        jwtPayload,
        isLoading,
        isInitialized,
        isSubmitting: formik.isSubmitting,
        showSuccessModal,
        documentTypes,

        formik,

        addEmptyDetail,
        removeDetail,
        updateDetail,
        addEmptyAdditionalField,
        removeAdditionalField,
        updateAdditionalField,

        handleCompanyChange,
        handleEstablishmentChange,
        handleEmissionPointChange,

        setDetails,
        setAdditionalFields,
        setShowSuccessModal,
    };
};

export default useRemissionGuide;
